import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/lib/api-client';

export const LANDING_ROUTES = {
  /**
   * Configuracao, na area logada. Endereco proprio de proposito: um
   * `/config-landing-page` na raiz colidiria com o slug de um salao chamado
   * "config-landing-page" - risco baixo, e o React Router ja preferiria essa
   * rota estatica de qualquer forma.
   */
  CONFIG: '/config-landing-page',
  /**
   * A propria landing page do salao, sem sessao - direto na raiz (sem
   * prefixo), pro endereco que se manda ao cliente ficar curto. O React
   * Router sempre da preferencia a uma rota estatica (/login, /cadastro...)
   * sobre este `:slug` dinamico, entao as rotas do proprio app nunca ficam
   * inacessiveis; so um salao cujo slug batesse com uma delas ficaria sem
   * pagina limpa - caso raro, e o endereco tecnico continua existindo.
   */
  PUBLIC_PATTERN: '/:slug',
  publicPage: (slug: string) => `/${slug}`,
} as const;

const ENDPOINT = '/api/v1/landing';
const PUBLIC = '/api/v1/public';

export type LandingAccent = 'VIOLET' | 'ROSE' | 'EMERALD' | 'AMBER' | 'SLATE';
export type Gender = 'MALE' | 'FEMALE' | 'UNDISCLOSED';

export interface LandingConfig {
  slug: string;
  isPublished: boolean;
  accent: LandingAccent;
  headline: string;
  tagline: string;
  about: string;
  /** Cada banner do topo com a frase dele. */
  slides: { image: string; title: string; subtitle: string }[];
  whatsapp: string;
  whatsappMessage: string;
  email: string;
  address: string;
  city: string;
  instagramUrl: string;
  facebookUrl: string;
  highlightIds: string[];
  publicUrl: string;
}

export interface PublicLanding {
  slug: string;
  accent: LandingAccent;
  headline: string;
  tagline: string | null;
  about: string | null;
  slides: { image: string; title: string; subtitle: string }[];
  whatsapp: string | null;
  whatsappMessage: string | null;
  email: string | null;
  address: string | null;
  city: string | null;
  instagramUrl: string | null;
  facebookUrl: string | null;
  company: { id: string; name: string; businessType: string; logo: string | null };
  units: {
    id: string;
    name: string;
    city: string | null;
    address: string | null;
    phone: string | null;
  }[];
  services: {
    id: string;
    name: string;
    description: string | null;
    categoryName: string | null;
    price: number;
    durationMinutes: number;
    image: string | null;
    highlighted: boolean;
  }[];
  /** O que a vitrine mostra: a categoria, nao o cardapio inteiro. */
  categories: {
    id: string;
    name: string;
    description: string | null;
    image: string | null;
    servicesCount: number;
    fromPrice: number;
  }[];
  professionals: { id: string; name: string }[];
}

/** Nome e telefone chegam mascarados do servidor; o valor inteiro nao vem. */
export interface CustomerLookup {
  found: boolean;
  maskedName: string | null;
  maskedPhone: string | null;
  token: string | null;
}

export const landingService = {
  getConfig: () => apiClient.get<LandingConfig>(ENDPOINT),
  saveConfig: (input: Omit<LandingConfig, 'publicUrl'>) =>
    apiClient.put<LandingConfig>(ENDPOINT, input),

  getPublic: (slug: string) => apiClient.get<PublicLanding>(`${PUBLIC}/landing/${slug}`),

  lookup: (slug: string, phone: string) =>
    apiClient.post<CustomerLookup>(`${PUBLIC}/customers/lookup`, { slug, phone }),

  register: (input: {
    slug: string;
    phone: string;
    name: string;
    gender: Gender;
    birthYear: number;
  }) =>
    apiClient.post<{ token: string; maskedName: string; maskedPhone: string }>(
      `${PUBLIC}/customers`,
      input,
    ),

  book: (input: {
    slug: string;
    token: string;
    unitId: string;
    notes?: string;
    items: { serviceId: string; professionalId?: string; startAt: string }[];
  }) => apiClient.post<PublicBooking>(`${PUBLIC}/appointments`, input),

  /** O agendamento por tras do link do WhatsApp - nome do cliente mascarado. */
  getBooking: (slug: string, id: string) =>
    apiClient.get<PublicBookingDetail>(
      `${PUBLIC}/appointments/${id}?slug=${encodeURIComponent(slug)}`,
    ),

  /** Autosave da pagina publica: mesmo token, agendamento que ja existe. */
  rebook: (
    id: string,
    input: {
      slug: string;
      token: string;
      unitId?: string;
      notes?: string;
      items?: { serviceId: string; professionalId?: string; startAt: string }[];
    },
  ) => apiClient.patch<PublicBooking>(`${PUBLIC}/appointments/${id}`, input),
};

export interface PublicBooking {
  id: string;
  startAt: string;
  endAt: string;
  status: string;
  total: number;
  /** Vale so para este horario - e o que autoriza altera-lo depois. */
  token?: string;
}

export interface PublicBookingDetail {
  id: string;
  unitId: string;
  unitName: string;
  maskedCustomerName: string;
  status: string;
  startAt: string;
  endAt: string;
  notes: string | null;
  total: number;
  items: {
    serviceId: string;
    serviceName: string;
    professionalId: string | null;
    professionalName: string | null;
    startAt: string;
    durationMinutes: number;
    price: number;
  }[];
}

const KEYS = { config: ['landing', 'config'] as const, public: ['landing', 'public'] as const };

/** `enabled: false` na pagina publica: la nao ha sessao para consultar a config. */
export function useLandingConfig(options: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: KEYS.config,
    queryFn: landingService.getConfig,
    enabled: options.enabled ?? true,
  });
}

export function useSaveLandingConfig() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: landingService.saveConfig,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: KEYS.config }),
  });
}

export function usePublicLanding(slug: string | undefined) {
  return useQuery({
    queryKey: [...KEYS.public, slug ?? ''],
    queryFn: () => landingService.getPublic(slug as string),
    enabled: Boolean(slug),
  });
}
